/**
 * creature/templates/training_dummy.ts — The Silent Teachers
 *
 * Training dummies are not alive. They don't breathe, don't run, don't
 * fight back. But they stand there, patiently, absorbing every blow you
 * throw. They are the most honest sparring partners you'll ever have.
 *
 * Four tiers of dummy, each with increasing defense:
 *   - Wooden: soft pine, low defense, great for beginners learning combos
 *   - Straw: burlap and hay, medium defense, wobbles on hit
 *   - Iron: reinforced plate, high defense, tests real damage output
 *   - Armored: enchanted steel, boss-level defense, for the serious
 *
 * Uses the biped body plan but with a unique "dummy" rendering path:
 *   - No animation (they don't walk)
 *   - No ears, no tail, no eyes (they're mannequins)
 *   - Wooden post body, crossbar arms, round head
 *   - Colors define the material: pine, burlap, grey iron, dark steel
 */

import type { SkeletonDef } from "../skeleton.js";
import type { SpeciesTemplate, GaitPattern } from "../species.js";
import { registerSpecies, createBreed } from "../species.js";

// ─── Dummy Skeleton (minimal — just a post with crossbar) ────────────────

const DUMMY_SKELETON: SkeletonDef = {
  id: "skeleton_training_dummy",
  rootJointId: "hip",
  joints: [
    {
      id: "hip", parentId: null,
      restOffset: { x: 0, y: 0 }, restAngle: -0.15,
      minAngle: -0.3, maxAngle: 0.3, length: 10,
      tags: ["root", "hip"],
    },
    {
      id: "shoulder", parentId: "hip",
      restOffset: { x: 0, y: 0 }, restAngle: 0,
      minAngle: -0.1, maxAngle: 0.1, length: 6,
      tags: ["shoulder", "spine"],
    },
    {
      id: "head", parentId: "shoulder",
      restOffset: { x: 0, y: 0 }, restAngle: 0,
      minAngle: -0.1, maxAngle: 0.1, length: 0,
      tags: ["head"],
    },
    // Arms are the crossbar
    {
      id: "arm_l", parentId: "shoulder",
      restOffset: { x: -3, y: 0 }, restAngle: Math.PI / 2,
      minAngle: 0.5, maxAngle: 2.0, length: 6,
      tags: ["arm"],
    },
    {
      id: "arm_r", parentId: "shoulder",
      restOffset: { x: 3, y: 0 }, restAngle: -Math.PI / 2,
      minAngle: -2.0, maxAngle: -0.5, length: 6,
      tags: ["arm"],
    },
    // Base post
    {
      id: "leg_l", parentId: "hip",
      restOffset: { x: -1, y: 0 }, restAngle: 0.1,
      minAngle: 0, maxAngle: 0.2, length: 8,
      tags: ["leg", "leg_front_l"],
    },
    {
      id: "leg_r", parentId: "hip",
      restOffset: { x: 1, y: 0 }, restAngle: -0.1,
      minAngle: -0.2, maxAngle: 0, length: 8,
      tags: ["leg", "leg_front_r"],
    },
  ],
  drawOrder: [
    { name: "back_legs",   joints: ["hip"] },
    { name: "body",        joints: ["hip", "shoulder"] },
    { name: "front_legs",  joints: ["shoulder"] },
    { name: "head",        joints: ["head"] },
    { name: "fire_glow",   joints: ["hip", "shoulder"] },
  ],
  ikChains: [],
};

// ─── Gaits (dummies don't move, but need a minimal gait entry) ──────────

const DUMMY_GAITS: GaitPattern[] = [
  {
    name: "stand",
    speedRange: [0, 0.1],
    legPhaseOffsets: { leg_l: 0, leg_r: 0 },
    strideLength: 0,
    bodyBob: 0,
    bodyRoll: 0,
    headBob: 0,
  },
];

// ─── Base Dummy Template ────────────────────────────────────────────────────

const DUMMY_BASE: SpeciesTemplate = {
  id: "training_dummy_wooden",
  displayName: "Wooden Training Dummy",
  bodyPlan: "biped",
  skeleton: DUMMY_SKELETON,
  size: "medium",
  baseSpeed: 0,
  heightAtShoulder: 14,
  gaits: DUMMY_GAITS,
  turnRate: 0,
  palette: {
    body: "#b8945c",      // warm pine wood
    markings: "#8a6a3a",  // darker wood grain
    eyes: "#3a3020",      // painted dots for eyes
    nose: "#6a5030",      // wood knot
    accent: "#c0a060",    // light wood highlights
    belly: "#a08040",     // lighter front
  },
  bodyShape: {
    bodyLength: 8,
    bodyWidth: 4,
    headRadius: 3.5,
    tailSegments: 0,
    tailLength: 0,
    earStyle: "none",
    earSize: 0,
    legThickness: 2.5,
    hasMane: false,
    hasWings: false,
    neckLength: 0.4,
    snoutLength: 0,
    legLength: 0.8,
  },
  behaviorDefaults: {
    energy: 0,
    sociability: 0,
    curiosity: 0,
    obedience: 0,
    playfulness: 0,
    courage: 0,
    agility: 0,
    stamina: 1.0,      // max HP multiplier — wooden = low
    nocturnality: 0,
    territoriality: 0,
    packInstinct: 0,
  },
  rideable: false,
  riderAttachJoint: "shoulder",
  riderOffsetZ: 0,
  saddleable: false,
  sounds: {
    idle: [],
    happy: [],
    hungry: [],
    walk: [],
    run: [],
    special: ["thud_wood"],  // impact sound
  },
  moodContribution: {
    warmth: 0,
    coziness: 0,
    energy: 0.1,
    nature: 0,
  },
};

// ─── Register Dummy Variants ────────────────────────────────────────────────

registerSpecies(DUMMY_BASE);

registerSpecies(createBreed(DUMMY_BASE, {
  id: "training_dummy_straw",
  displayName: "Straw Training Dummy",
  palette: {
    body: "#c8a848",      // golden straw
    markings: "#a08830",  // darker straw binding
    eyes: "#2a2018",      // button eyes
    nose: "#907828",      // twine nose
    accent: "#d8c060",    // bright straw tips
    belly: "#b89838",     // worn burlap
  },
  behaviorDefaults: {
    ...DUMMY_BASE.behaviorDefaults,
    stamina: 0.6,         // medium HP
  },
}));

registerSpecies(createBreed(DUMMY_BASE, {
  id: "training_dummy_iron",
  displayName: "Iron Training Dummy",
  palette: {
    body: "#808890",      // grey iron
    markings: "#606870",  // darker steel rivets
    eyes: "#a04020",      // glowing ember eyes
    nose: "#505860",      // iron plate
    accent: "#98a0a8",    // polished highlights
    belly: "#707880",     // belly plate
  },
  bodyShape: {
    ...DUMMY_BASE.bodyShape,
    bodyWidth: 5,         // beefier
    legThickness: 3,      // thicker base
  },
  behaviorDefaults: {
    ...DUMMY_BASE.behaviorDefaults,
    stamina: 0.8,         // high HP
    courage: 0.5,         // some resistance
  },
}));

registerSpecies(createBreed(DUMMY_BASE, {
  id: "training_dummy_armored",
  displayName: "Armored Training Dummy",
  palette: {
    body: "#404850",      // dark steel
    markings: "#2a3038",  // obsidian seams
    eyes: "#ff6020",      // fiery eyes
    nose: "#383e48",      // face plate
    accent: "#c08020",    // gold trim
    belly: "#484e58",     // armored front
  },
  bodyShape: {
    ...DUMMY_BASE.bodyShape,
    bodyWidth: 6,         // heavily built
    headRadius: 4,        // helmeted
    legThickness: 3.5,    // massive base
  },
  behaviorDefaults: {
    ...DUMMY_BASE.behaviorDefaults,
    stamina: 1.0,         // maximum HP
    courage: 1.0,         // very high resistance
  },
}));
